import { z } from 'zod';
import { AddressSchema, AuditSchema, DateOnlySchema, IdSchema } from './common.ts';

export const CONTACT_KINDS = ['customer', 'sub', 'vendor'] as const;
export const ContactKindSchema = z.enum(CONTACT_KINDS);
export type ContactKind = z.infer<typeof ContactKindSchema>;

/**
 * Proof a sub carries insurance, with the date it lapses.
 *
 * The expiry is the point of the record: an expired certificate on an active
 * job is a liability the contractor is carrying without knowing, which is why
 * it surfaces in dashboard action items rather than sitting in a file.
 */
export const CertificateSchema = z.object({
  id: IdSchema,
  kind: z.enum(['generalLiability', 'workersComp', 'auto', 'umbrella', 'other']),
  carrier: z.string().optional(),
  policyNumber: z.string().optional(),
  expiresAt: DateOnlySchema,
  fileId: IdSchema.optional(),
});
export type Certificate = z.infer<typeof CertificateSchema>;

export const ContactSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  kind: ContactKindSchema,
  /** Person's name, or the trading name for a company. */
  name: z.string().min(1),
  companyName: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  address: AddressSchema.partial().optional(),
  /** Trade or category, e.g. "Framing", "Plumbing", "Appliances". */
  trade: z.string().optional(),
  notes: z.string().optional(),
  status: z.enum(['active', 'inactive']).default('active'),
  certificates: z.array(CertificateSchema).default([]),
  defaultCostCodeIds: z.array(IdSchema).default([]),
  /** Set once the contact has been given portal access. */
  memberUid: IdSchema.optional(),
  /**
   * A contact the product created and depends on (INV2-05, SCRUM-267).
   *
   * There is exactly one today: **Unknown vendor**, which is where
   * stock that was found in the yard points when nobody knows who
   * supplied it. It is a real contact rather than a nullable field so
   * that every report, filter and join keeps working — the alternative
   * is a special case in each of them, and the first one anybody
   * forgets is the one that matters.
   *
   * The rules refuse to delete a contact carrying this flag. Deleting
   * it would orphan every item pointing at it, which is the same
   * argument `cost-code.ts` makes about deleting a code.
   *
   * **Optional rather than defaulted to `false`**, so that an ordinary
   * contact simply does not carry the field: every document already
   * written is correct as it stands, and nothing has to be migrated to
   * say "no" to a question nobody was asking. Read it as `=== true`.
   */
  system: z.boolean().optional(),
});
export type Contact = z.infer<typeof ContactSchema>;

/**
 * Days until the soonest certificate lapses; null when none are recorded.
 * Negative means already expired.
 */
export function daysUntilCertificateExpiry(
  contact: Pick<Contact, 'certificates'>,
  today: string,
): number | null {
  if (contact.certificates.length === 0) return null;
  const soonest = contact.certificates
    .map((certificate) => certificate.expiresAt)
    .sort()
    .at(0)!;
  const msPerDay = 86_400_000;
  return Math.round((Date.parse(soonest) - Date.parse(today)) / msPerDay);
}
